import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentUser } from 'store/user/selectors';
import { selectSubscriptions } from 'store/subscriptions/selectors';
import { pauseSubscription, resumeSubscription } from 'store/subscriptions/actions';

import { PRODUCT_CODES } from 'constants/payments';

import {
  OfferTypes,
  cancelFlow_offerResultScreenLoad,
  cancelFlow_subscriptionClick,
  cancelFlow_offerResultClick,
} from 'services/analytics/trackers/cancelFlow';

import { useModals } from 'hooks';

import { getFormattedDateByCountry } from 'helpers/date';
import { normalizeDateByLocale } from 'helpers/normalizeDateByLocale';

import { ISubscription } from 'types/subscription';
import { UserStore } from 'types/store/userStore';
import { ModalName } from 'types/modals';
import { pauseFlow_pauseResultClick, pauseFlow_pauseResultScreenLoad } from 'services/analytics/trackers/pauseFlow';

const PAUSE_TYPE = 'pause';
const FREE_MONTH_TYPE = 'free_month';

const getMainSubscription = (subscriptions: ISubscription[]) => {
  return subscriptions.find((s) => s.product_code === PRODUCT_CODES.APP_FULL_ACCESS || s.is_upgrade);
};

type HandleErrorType = {
  openModal: (modalId: ModalName, props?: any) => any;
  subscriptionId: string;
  modalTitle: string;
  onPauseSubscription: (isPauseFlow?: boolean) => void;
};

const handlePauseSubscriptionError = ({
  openModal,
  isPauseFlow,
  subscriptionId,
  modalTitle,
  onPauseSubscription,
}: HandleErrorType & {
  isPauseFlow: boolean;
}) => {
  openModal(ModalName.WARNING_MODAL, {
    offerType: OfferTypes.Pause_offer,
    onMainBtnClick: () => {
      const commonData = {
        action: 'try_again' as const,
        result: 'fail' as const,
        subscription_id: subscriptionId,
      };

      if (isPauseFlow) {
        pauseFlow_pauseResultClick(commonData);
      } else {
        cancelFlow_offerResultClick({ ...commonData, offer_type: OfferTypes.Pause_offer });
      }

      onPauseSubscription();
    },
    onSupportClick: () => {
      const commonData = {
        action: 'contact_support' as const,
        result: 'fail' as const,
        subscription_id: subscriptionId,
      };

      if (isPauseFlow) {
        pauseFlow_pauseResultClick(commonData);
      } else {
        cancelFlow_offerResultClick({ ...commonData, offer_type: OfferTypes.Pause_offer });
      }
    },
    title: modalTitle,
    analyticScreenLoadHandler() {
      if (isPauseFlow) {
        pauseFlow_pauseResultScreenLoad({ result: 'fail', subscription_id: subscriptionId });
      } else {
        cancelFlow_offerResultScreenLoad({
          result: 'fail',
          offer_type: OfferTypes.Pause_offer,
          subscription_id: subscriptionId,
        });
      }
    },
  });
};

const handleGetFreeMonthPause = ({ openModal, subscriptionId, modalTitle, onPauseSubscription }: HandleErrorType) => {
  openModal(ModalName.WARNING_MODAL, {
    offerType: OfferTypes.Free_month,
    onMainBtnClick: () => {
      cancelFlow_offerResultClick({
        offer_type: OfferTypes.Free_month,
        action: 'try_again',
        result: 'fail',
        subscription_id: subscriptionId,
      });
      onPauseSubscription();
    },
    onSupportClick: () =>
      cancelFlow_offerResultClick({
        offer_type: OfferTypes.Free_month,
        action: 'contact_support',
        result: 'fail',
        subscription_id: subscriptionId,
      }),
    title: modalTitle,
    analyticScreenLoadHandler() {
      cancelFlow_offerResultScreenLoad({
        result: 'fail',
        offer_type: OfferTypes.Free_month,
        subscription_id: subscriptionId,
      });
    },
  });
};

const handleResumeSubscriptionError = ({
  openModal,
  onPauseSubscription,
  subscriptionId,
  modalTitle,
}: HandleErrorType) => {
  openModal(ModalName.WARNING_MODAL, {
    offerType: OfferTypes.Pause_offer,
    onMainBtnClick: () => {
      cancelFlow_offerResultClick({
        offer_type: OfferTypes.Pause_offer,
        action: 'try_again',
        result: 'fail',
        subscription_id: subscriptionId,
      });
      onPauseSubscription();
    },
    onSupportClick: () =>
      cancelFlow_offerResultClick({
        offer_type: OfferTypes.Pause_offer,
        action: 'contact_support',
        result: 'fail',
        subscription_id: subscriptionId,
      }),
    title: modalTitle,
    analyticScreenLoadHandler() {
      cancelFlow_offerResultScreenLoad({
        result: 'fail',
        offer_type: OfferTypes.Pause_offer,
        subscription_id: subscriptionId,
      });
    },
  });
};

const usePauseSubscription = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { openModal } = useModals();
  const { subscriptions } = useSelector(selectSubscriptions);

  const mainSubscription = getMainSubscription(subscriptions);
  const currentUser = useSelector(selectCurrentUser) as UserStore;
  const pauseToDate = getFormattedDateByCountry(mainSubscription?.pause.pause_to || '', currentUser.country!);

  const onPauseSubscription = useCallback(
    (isPauseFlow = false) => {
      openModal(ModalName.WAITING_MODAL, { title: 'subscription.modals.updating' });

      if (mainSubscription) {
        dispatch(
          pauseSubscription({
            id: mainSubscription.external_id,
            provider: mainSubscription.payment_provider,
            pause_type: PAUSE_TYPE,
            onSuccess: (subscriptions: ISubscription[]) => {
              const sub = getMainSubscription(subscriptions);
              const pauseToDate = getFormattedDateByCountry(sub?.pause.pause_to || '', currentUser.country!);

              openModal(ModalName.SUCCESS_MODAL, {
                externalId: mainSubscription.external_id,
                offerType: isPauseFlow ? null : OfferTypes.Pause_offer,
                title: t('subscription.cancelFlow.pauseOffer.pause.successModal.title'),
                subtitle: t('subscription.cancelFlow.pauseOffer.pause.successModal.subtitle', { pauseToDate }),
                isPauseFlow,
              });
            },
            onError: () =>
              handlePauseSubscriptionError({
                openModal,
                onPauseSubscription,
                modalTitle: t('subscription.cancelFlow.errorModal.title'),
                isPauseFlow,
                subscriptionId: mainSubscription?.external_id,
              }),
          })
        );
      }
    },
    [mainSubscription, currentUser]
  );

  const onGetFreeMonthPause = useCallback(() => {
    openModal(ModalName.WAITING_MODAL, { title: 'subscription.modals.updating' });

    if (mainSubscription) {
      dispatch(
        pauseSubscription({
          id: mainSubscription.external_id,
          provider: mainSubscription.payment_provider,
          pause_type: FREE_MONTH_TYPE,
          onSuccess: (subscriptions: ISubscription[]) => {
            const sub = getMainSubscription(subscriptions);
            const renewalDate = sub?.expired_date
              ? normalizeDateByLocale({
                  date: sub.expired_date,
                  country: currentUser.country,
                  timezone: currentUser.timezone,
                })
              : '';

            openModal(ModalName.SUCCESS_MODAL, {
              icon: 'success',
              externalId: mainSubscription.external_id,
              offerType: OfferTypes.Free_month,
              title: t('subscription.cancelFlow.monthFreeOffer.successModal.title'),
              subtitle: t('subscription.cancelFlow.pauseOffer.successModal.subtitle', { renewalDate }),
            });
          },
          onError: () =>
            handleGetFreeMonthPause({
              openModal,
              onPauseSubscription,
              subscriptionId: mainSubscription?.external_id,
              modalTitle: t('subscription.cancelFlow.errorModal.title'),
            }),
        })
      );
    }
  }, [mainSubscription, currentUser]);

  const onResumeSubscription = useCallback(() => {
    openModal(ModalName.WAITING_MODAL, { title: 'subscription.modals.updating' });

    if (mainSubscription) {
      cancelFlow_subscriptionClick({
        status: mainSubscription.state,
        action: 'resume',
        reminder: !!mainSubscription.reminder,
        free_month: !!mainSubscription.discounts.free_month,
        subscription_id: mainSubscription.external_id,
      });

      dispatch(
        resumeSubscription({
          id: mainSubscription.external_id,
          provider: mainSubscription.payment_provider,
          onSuccess: (subscriptions: ISubscription[]) => {
            const sub = getMainSubscription(subscriptions);
            const renewalDate = getFormattedDateByCountry(sub?.expired_date || '', currentUser.country!);

            openModal(ModalName.SUCCESS_MODAL, {
              externalId: mainSubscription.external_id,
              title: t('subscription.cancelFlow.pauseOffer.resume.successModal.title'),
              subtitle: t('subscription.cancelFlow.pauseOffer.successModal.subtitle', { renewalDate }),
              offerType: OfferTypes.Pause_offer,
            });
          },
          onError: () =>
            handleResumeSubscriptionError({
              openModal,
              subscriptionId: mainSubscription?.external_id,
              onPauseSubscription,
              modalTitle: t('subscription.cancelFlow.errorModal.title'),
            }),
        })
      );
    }
  }, [mainSubscription, currentUser]);

  return {
    pauseToDate,
    subscriptionId: mainSubscription?.external_id,
    onPauseSubscription,
    onResumeSubscription,
    onGetFreeMonthPause,
  };
};

export default usePauseSubscription;
